import { ReactNode } from 'react';

import Fade from '@mui/material/Fade';

import { AppError } from '@vk-hr-tek/core/error';
import { Box } from '@vk-hr-tek/ui/Box';
import { ContainerWithScrollToTopButton } from '@vk-hr-tek/app-ui/ContainerWithScrollToTopButton';
import { HTTP_INTERNAL_SERVER_ERROR_STATUS } from '@vk-hr-tek/core/http';

import { PageError } from './PageError';
import { PageHeader } from './PageHeader';
import { PageLoading } from './PageLoading';
import { PageLoadingText } from './PageLoadingText';

const TOP_SCROLL_MARGIN = 100;

/**
 * Возможные статусы страницы.
 */
export type PageStatus =
  | 'idle'
  | 'loading'
  | 'loading-text'
  | 'complete'
  | 'failed'
  | 'aborted';

/**
 * Cвойства компонента {@link Page}.
 */
interface PageProps {
  /**
   * Основные действия, отображаемые в заголовке страницы.
   */
  actions?: React.ReactNode | React.ReactNode[];

  /**
   * Дополнительные действия, отображаемые в заголовке страницы.
   */
  additionalActions?: React.ReactNode | React.ReactNode[];

  /**
   * Содержимое страницы.
   */
  children: React.ReactNode | React.ReactNode[];

  /**
   * Ошибка, возникшая при загрузке страницы.
   */
  error: AppError | null;

  /**
   * Ссылка, отображаемая в заголовке страницы.
   */
  link?: ReactNode;

  /**
   * Текст, отображаемый при загрузке.
   */
  loadingText?: ReactNode;

  /**
   * Компонент, отображаемый при загрузке.
   */
  loadingComponent?: ReactNode;

  /**
   * Определяет, должен ли быть скрыт индикатор загрузки.
   *
   * @defaultValue `false`
   */
  hideLoader?: boolean;

  /**
   * Определяет, должен ли заголовок отображаться на мобильных устройствах.
   *
   * @defaultValue `false`
   */
  showTitleMobile?: boolean;

  /**
   * Статус страницы.
   */
  status: PageStatus;

  /**
   * Заголовок страницы.
   */
  title: ReactNode;

  /**
   * Позиция действий в заголовке страницы.
   *
   * @defaultValue `'default'`
   */
  actionsPosition?: 'default' | 'absolute';

  /**
   * Определяет, должен ли контент страницы отображаться в виде виджета.
   *
   * @defaultValue `false`
   */
  asWidget?: boolean;

  /**
   * Определяет, отображать ли кнопку прокрутки наверх.
   *
   * @defaultValue `false`
   */
  withScrollToTopButton?: boolean;
}

/**
 * Страница с заголовком, индикаторами загрузки и обработкой ошибок.
 *
 * @public
 * @param actions - Основные действия, отображаемые в заголовке страницы.
 * @param additionalActions - Дополнительные действия, отображаемые в заголовке страницы.
 * @param children - Содержимое страницы.
 * @param error - Ошибка, возникшая при загрузке страницы.
 * @param link - Ссылка, отображаемая в заголовке страницы.
 * @param loadingText - Текст, отображаемый при загрузке.
 * @param hideLoader - Определяет, должен ли быть скрыт индикатор загрузки.
 * @param showTitleMobile - Определяет, должен ли заголовок отображаться на мобильных устройствах.
 * @param status - Статус страницы.
 * @param title - Заголовок страницы.
 * @param actionsPosition - Позиция действий в заголовке страницы.
 * @param asWidget - Определяет, должен ли контент страницы отображаться в виде виджета.
 * @param withScrollToTopButton - Определяет, отображать ли кнопку прокрутки наверх.
 * @returns Компонент Page.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link PageProps}
 */
export const Page = ({
  actions = null,
  additionalActions = null,
  children,
  error,
  link,
  loadingText,
  loadingComponent,
  hideLoader = false,
  showTitleMobile = false,
  status,
  title: titleNode,
  actionsPosition = 'default',
  asWidget = false,
  withScrollToTopButton = false,
}: PageProps) => {
  const title = (
    <PageHeader
      actions={actions}
      additionalActions={additionalActions}
      link={link}
      showTitleMobile={showTitleMobile}
      title={titleNode}
      actionsPosition={actionsPosition}
      asWidget={asWidget}
    />
  );

  if (status === 'idle') {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{title}</>;
  }

  if (status === 'loading') {
    return (
      <>
        {title}
        <Fade timeout={1000} in>
          <div>
            <PageLoading loadingComponent={loadingComponent} />
          </div>
        </Fade>
      </>
    );
  }

  if (status === 'loading-text' || status === 'aborted') {
    return (
      <>
        {title}
        <Fade timeout={1000} in>
          <PageLoadingText text={loadingText} hideLoader={hideLoader} />
        </Fade>
      </>
    );
  }

  if (status === 'failed') {
    return (
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        minHeight={0}
        height="100%"
      >
        {title}
        <Fade timeout={1000} in>
          <Box display="flex" flexDirection="column" flexGrow={1} minHeight={0}>
            <PageError
              status={error ? error.status : HTTP_INTERNAL_SERVER_ERROR_STATUS}
              traceId={error?.traceId}
            />
          </Box>
        </Fade>
      </Box>
    );
  }

  if (withScrollToTopButton) {
    return (
      <ContainerWithScrollToTopButton scrollMargin={TOP_SCROLL_MARGIN}>
        {title}
        <Fade timeout={1000} in>
          <Box>{children}</Box>
        </Fade>
      </ContainerWithScrollToTopButton>
    );
  }

  return (
    <>
      {title}
      <Fade timeout={1000} in>
        <Box>{children}</Box>
      </Fade>
    </>
  );
};
